import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { message } from 'antd';
import i18n from '../i18n';
import { getErrorMessage } from '../utils/i18n/getErrorMessage';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import type { PickupCity } from '../types/pickupCity';

function mapCity(id: string, data: Record<string, unknown>): PickupCity {
  return {
    id,
    label: String(data.label || ''),
    districts: Array.isArray(data.districts)
      ? data.districts.map((d) => String(d).trim()).filter(Boolean)
      : [],
    country: data.country ? String(data.country) : 'CM',
    active: data.active !== false,
    sortOrder: typeof data.sortOrder === 'number' ? data.sortOrder : 0,
    createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.(),
    updatedAt: (data.updatedAt as { toDate?: () => Date })?.toDate?.(),
  };
}

/** Villes actives (page publique + config pressing). */
export function usePickupCities(options?: { activeOnly?: boolean }) {
  const activeOnly = options?.activeOnly ?? true;
  return useQuery({
    queryKey: ['pickupCities', activeOnly ? 'active' : 'all'],
    queryFn: async (): Promise<PickupCity[]> => {
      if (!db) return [];
      const col = collection(db, COLLECTIONS.pickupCities);
      const snap = await getDocs(query(col, orderBy('sortOrder', 'asc')));
      const cities = snap.docs.map((d) => mapCity(d.id, d.data()));
      return activeOnly ? cities.filter((c) => c.active) : cities;
    },
    enabled: !!db,
  });
}

export function useCreatePickupCity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      label: string;
      districts: string[];
      sortOrder?: number;
      active?: boolean;
      country?: string;
    }) => {
      if (!db) throw new Error('Firebase indisponible');
      await addDoc(collection(db, COLLECTIONS.pickupCities), {
        label: input.label.trim(),
        districts: input.districts,
        sortOrder: input.sortOrder ?? 0,
        active: input.active !== false,
        country: input.country || 'CM',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pickupCities'] });
      message.success(i18n.t('pickup.cityAdded', { ns: 'operations' }));
    },
    onError: (e: Error) =>
      message.error(getErrorMessage(e) || i18n.t('pickup.cityCreateError', { ns: 'operations' })),
  });
}

export function useUpdatePickupCity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      label?: string;
      districts?: string[];
      sortOrder?: number;
      active?: boolean;
    }) => {
      if (!db) throw new Error('Firebase indisponible');
      const patch: {
        updatedAt: ReturnType<typeof serverTimestamp>;
        label?: string;
        districts?: string[];
        sortOrder?: number;
        active?: boolean;
      } = { updatedAt: serverTimestamp() };
      if (input.label !== undefined) patch.label = input.label.trim();
      if (input.districts !== undefined) patch.districts = input.districts;
      if (input.sortOrder !== undefined) patch.sortOrder = input.sortOrder;
      if (input.active !== undefined) patch.active = input.active;
      await updateDoc(doc(db, COLLECTIONS.pickupCities, input.id), patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pickupCities'] });
      message.success(i18n.t('pickup.cityUpdated', { ns: 'operations' }));
    },
    onError: (e: Error) =>
      message.error(getErrorMessage(e) || i18n.t('pickup.cityUpdateError', { ns: 'operations' })),
  });
}

export function useDeletePickupCity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (!db) throw new Error('Firebase indisponible');
      await deleteDoc(doc(db, COLLECTIONS.pickupCities, id));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pickupCities'] });
      message.success(i18n.t('pickup.cityDeleted', { ns: 'operations' }));
    },
    onError: (e: Error) => message.error(getErrorMessage(e) || i18n.t('pickup.cityUpdateError', { ns: 'operations' })),
  });
}
